import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, ChevronDown, Coins, CreditCard, Crown, Loader2, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import AgentTabHeader from '../agents/tabs/AgentTabHeader';
import toastConfig from '../config/Toast';
import useApi from '../api';
import PlanPicker from '../billing/PlanPicker';
import useCheckout from '../billing/useCheckout';
import { formatCredits, formatUsd } from '../billing/format';
import type { BillingSummary } from '../billing/types';

const FREE_LICENSE_TYPES = ['trial', 'free'];
const PREMIUM_LEGACY_PLANS = ['business', 'activate', 'enterprise', 'aimdoc'];

const formatDate = (isoString: string | null | undefined): string | null => {
  if (!isoString) return null;
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return null;
  }
};

const describeLegacyPlan = (plan: string): string => {
  if (plan === 'activate') return 'Full access including onboarding';
  if (PREMIUM_LEGACY_PLANS.includes(plan)) return 'Full access to all features';
  return 'Basic features';
};

const BillingTab: React.FC = () => {
  const { get, post } = useApi();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [grantedCredits, setGrantedCredits] = useState<number | null>(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await get('/billing/summary');
      const data = response?.data as BillingSummary;
      setSummary(data);
      setPickerOpen(!data?.is_credits_plan);
    } catch (error) {
      console.error('Error loading billing summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [get]);

  const fetchGranted = useCallback(async () => {
    try {
      const response = await get('/usage/credits');
      const granted = response?.data?.allotment?.granted;
      setGrantedCredits(typeof granted === 'number' && granted > 0 ? granted : null);
    } catch {
      setGrantedCredits(null);
    }
  }, [get]);

  const { checkout, loading: checkoutLoading } = useCheckout({
    onChanged: () => {
      void fetchSummary();
      void fetchGranted();
    },
  });

  useEffect(() => {
    void fetchSummary();
    void fetchGranted();
  }, [fetchSummary, fetchGranted]);

  // Return from Stripe Checkout / an in-place change lands on ?billing=…
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (!billing) return;
    if (billing === 'success') {
      toast.success('You’re all set — your credits are ready.', toastConfig);
      void fetchSummary();
      void fetchGranted();
    } else if (billing === 'changed') {
      toast.success('Your plan has been updated.', toastConfig);
      void fetchSummary();
      void fetchGranted();
    } else if (billing === 'cancelled') {
      toast.info('Checkout cancelled — nothing was charged.', toastConfig);
    }
    params.delete('billing');
    params.delete('session_id');
    const next = params.toString();
    window.history.replaceState({}, document.title, `${window.location.pathname}${next ? `?${next}` : ''}`);
  }, [fetchSummary, fetchGranted]);

  const handleManageBilling = async () => {
    setIsOpeningPortal(true);
    try {
      const response = await post('/billing/portal-session', {});
      const url = response?.data?.url;
      if (!url) throw new Error('No portal url returned');
      window.location.href = url;
    } catch (error: any) {
      console.error('Error opening billing portal:', error);
      toast.error(error?.detail || 'Unable to open billing portal. Please try again.', toastConfig);
      setIsOpeningPortal(false);
    }
  };

  const licenseType = summary?.license_type || 'trial';
  const isCredits = !!summary?.is_credits_plan && !!summary?.plan;
  const isFree = !isCredits && FREE_LICENSE_TYPES.includes(licenseType);
  const isLegacy = !isCredits && !isFree;
  const hasSubscription = !!summary?.has_subscription;
  const cancelScheduled = !!summary?.cancel_scheduled;
  const periodEndFormatted = cancelScheduled ? formatDate(summary?.period_end) : null;
  const balance = summary?.balance;
  const balanceEnforced = !!balance?.enforced;
  const balanceExhausted = balanceEnforced && !!balance?.exhausted;
  const balanceLow =
    balanceEnforced && !balanceExhausted && !!balance && balance.granted > 0
    && balance.remaining / balance.granted <= 0.2;

  const handleSelectPlan = async (planId: string) => {
    if (!summary) return;
    // PlanPicker only knows ids; the picker's own list has the name/credits,
    // but useCheckout wants them for analytics + toasts, so fetch once more.
    try {
      const response = await get('/billing/plans');
      const plan = (response?.data?.plans || []).find((p: { id: string }) => p.id === planId);
      if (!plan) throw new Error('Plan not found');
      await checkout(plan);
    } catch (error: any) {
      toast.error(error?.detail || 'Unable to start checkout. Please try again.', toastConfig);
    }
  };

  const renderCurrentPlan = () => {
    if (summaryLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your plan…
        </div>
      );
    }
    if (isCredits && summary?.plan) {
      return (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold">{summary.plan.name}</span>
            {!cancelScheduled && <Badge variant="secondary">Active</Badge>}
            {cancelScheduled && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-400">
                Cancels {periodEndFormatted}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {formatUsd(summary.plan.price_cents, summary.plan.currency)} / month ·{' '}
            {formatCredits(summary.plan.credits)} credits each month
            {summary.plan.bonus_pct > 0 ? ` (includes ${summary.plan.bonus_pct}% bonus)` : ''}
          </p>
        </>
      );
    }
    if (isFree) {
      return (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold">Free</span>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {grantedCredits
              ? `Your first ${formatCredits(grantedCredits)} credits are on us. Pick a monthly amount when you're ready to keep going.`
              : 'Your first credits are on us. Pick a monthly amount when you’re ready to keep going.'}
          </p>
        </>
      );
    }
    return (
      <>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-lg font-semibold capitalize">{licenseType}</span>
          <Badge variant="outline">Legacy plan</Badge>
          {PREMIUM_LEGACY_PLANS.includes(licenseType) && !cancelScheduled && (
            <Badge variant="secondary">Active</Badge>
          )}
          {cancelScheduled && (
            <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-400">
              Cancels {periodEndFormatted}
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {describeLegacyPlan(licenseType)}
          {summary?.monthly_credit_allotment
            ? ` · ${formatCredits(summary.monthly_credit_allotment)} credits each month`
            : ''}
        </p>
      </>
    );
  };

  const picker = (
    <PlanPicker
      currentPlanId={isCredits ? summary?.plan?.id : null}
      hasSubscription={hasSubscription}
      onSelect={handleSelectPlan}
      loading={checkoutLoading}
    />
  );

  return (
    <div>
      <AgentTabHeader
        icon={CreditCard}
        title="Billing"
        description="Your monthly credits, payment method, and invoices."
      />

      <div className="mx-auto w-full max-w-4xl space-y-6 py-6">
        {cancelScheduled && periodEndFormatted && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <p className="font-medium">Your subscription is scheduled to cancel on {periodEndFormatted}.</p>
              <p className="mt-1 text-muted-foreground">
                You&apos;ll stop receiving monthly credits after that date. Anything you still hold keeps
                working until it&apos;s used up. You can reactivate anytime from Manage Billing below.
              </p>
            </div>
          </div>
        )}

        {/* Credit balance (enforcement state) */}
        {balanceEnforced && balance && (
          <div
            className={`overflow-hidden rounded-lg border bg-card ${
              balanceExhausted ? 'border-destructive/40' : balanceLow ? 'border-amber-500/40' : ''
            }`}
          >
            <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Coins className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Credit balance
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className={`text-lg font-semibold ${balanceExhausted ? 'text-destructive' : ''}`}>
                      {formatCredits(balance.remaining)} remaining
                    </span>
                    {balance.granted > 0 && (
                      <span className="text-sm text-muted-foreground">of {formatCredits(balance.granted)}</span>
                    )}
                    {balanceExhausted && <Badge variant="destructive">Agents paused</Badge>}
                    {balanceLow && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-400">
                        Running low
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {balanceExhausted
                      ? isCredits
                        ? 'Your website agent is hidden and background runs are on hold until your next cycle starts or you add credits.'
                        : 'Your website agent is hidden and background runs are on hold. Choose a monthly amount below and everything resumes right away.'
                      : 'Every capability draws from the same balance; expensive work simply uses more credits.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current plan */}
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Crown className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Current plan
                </p>
                {renderCurrentPlan()}
              </div>
            </div>
          </div>

          {/* Plan picker */}
          <div className="border-t p-5">
            {isCredits ? (
              <Collapsible open={pickerOpen} onOpenChange={setPickerOpen}>
                <CollapsibleTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between sm:w-auto">
                    <span className="flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      Change plan
                    </span>
                    <ChevronDown
                      className={`ml-2 h-4 w-4 transition-transform ${pickerOpen ? 'rotate-180' : ''}`}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-5">
                  <p className="mb-4 text-sm text-muted-foreground">
                    Upgrades apply immediately — you pay the prorated difference and get the matching share of extra credits for the rest of this cycle. Downgrades
                    take effect at your next renewal — you keep this month&apos;s credits.
                  </p>
                  <div className="mx-auto max-w-md">{picker}</div>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <>
                <p className="mb-4 text-sm text-muted-foreground">
                  {isLegacy
                    ? 'You’re on a legacy plan — nothing changes unless you choose a monthly credit amount below.'
                    : 'Choose how many credits you want each month. Every capability is included; expensive work simply uses more credits.'}
                </p>
                <div className="mx-auto max-w-md">{picker}</div>
              </>
            )}
          </div>
        </div>

        {/* Stripe portal */}
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold">Manage billing with Stripe</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Update your payment method, view invoices, cancel or reactivate your subscription
                  through our secure Stripe billing portal.
                </p>
              </div>
            </div>
            {summary?.has_stripe_customer ? (
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  You&apos;ll be redirected to Stripe&apos;s hosted billing portal.
                </p>
                <Button
                  type="button"
                  onClick={handleManageBilling}
                  disabled={isOpeningPortal}
                  className="w-full gap-2 sm:w-auto"
                >
                  {isOpeningPortal ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Opening...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Manage Billing
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <p className="mt-5 text-sm text-muted-foreground">
                No billing details on file yet. Choose a monthly amount above to get started.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingTab;
